import { z } from 'zod';
import { HouseEdgeBps } from './money';
import { CurrencyCode, MinorUnits } from './money';

/**
 * Seamless (single) wallet contract. The operator owns the player balance and
 * implements these four endpoints; we are always the caller.
 *
 * Idempotency rule: `txId` is generated by us, is unique forever, and is
 * repeated verbatim on every retry of the same logical operation. An operator
 * that receives a `txId` it has already applied MUST return the original
 * result with `status: "OK"` instead of debiting twice.
 */

export const WalletBaseRequest = z.object({
  /** Identifies which integration secret signs the request. */
  operatorId: z.string().min(1),
  /** The operator's own player identifier, exactly as given at launch. */
  playerId: z.string().min(1),
  /** Our session id, so the operator can tie the call to a launch. */
  sessionId: z.string().min(1),
  currency: CurrencyCode,
  gameCode: z.string().min(1),
  /** Unique per HTTP attempt; changes on retry, unlike txId. */
  requestId: z.string().min(1),
  /** Epoch milliseconds as a string, also covered by the signature. */
  timestamp: z.string().min(1),
});

export const BalanceRequest = WalletBaseRequest;
export type BalanceRequest = z.infer<typeof BalanceRequest>;

/**
 * Which of a round's calls carries the edge to price it at.
 *
 * Most games settle their edge before the stake is taken, so every call in
 * the round agrees and the operator can act on the first one. Crash cannot:
 * the deduction depends on where the player cashes out, which nobody knows
 * at bet time, so its `/bet` is provisional and the closing `/win` is the
 * real number.
 *
 * This travels on the call rather than living in a per-game table the
 * operator keeps, because which rule a game follows is a property of how
 * that game prices risk, and that can change. PSR caps its stake low enough
 * today that exposure never moves its edge; lift the cap and it becomes a
 * settle-time number like crash's. When that happens PSR starts sending
 * `WIN_FINAL` and every operator is correct the same day, with no table to
 * update and no release to wait for.
 */
export const EdgeAuthority = z.enum([
  /** One edge for the whole round. Every `/bet` and the `/win` carry it, and
   * a disagreement between them is our bug, not a rule. Sum the debits for
   * the `roundId` to get the wager. */
  'BET_WIN_MUST_MATCH',
  /** The `/bet` edge is provisional. The closing `/win` prices the entry's
   * whole wager, and is never the lower of the two. */
  'WIN_FINAL',
]);
export type EdgeAuthority = z.infer<typeof EdgeAuthority>;

/**
 * The house edge the engine actually applied to this round, reported so the
 * operator can price rakeback against the same edge that produced the payout
 * rather than the catalogue average.
 *
 * It travels twice: `houseEdgeBps` is the value the engine used, `rtp` is that
 * same value as a percentage (`rtpPctFromEdgeBps`), which is the form most
 * operator ledgers are keyed on. All three are optional together: a game with
 * a fixed paytable reports no per-round edge, and a round with no edge has no
 * authority to declare either. Absent means "use the catalogue RTP", which is
 * not the same as a `houseEdgeBps` of zero — that is a real zero-edge round.
 */
const AppliedEdgeFields = {
  houseEdgeBps: HouseEdgeBps.optional(),
  rtp: z.number().min(0).max(100).optional(),
  /** Which call to price this round at. Present whenever the edge is. */
  edgeAuthority: EdgeAuthority.optional(),
};

export const BetRequest = WalletBaseRequest.extend({
  /** Our round id. Stable for the whole round including the win call. */
  roundId: z.uuid(),
  /** Idempotency key for this debit. */
  txId: z.uuid(),
  amount: MinorUnits,
  /** Present when the bet is funded by a bonus/free-round grant. */
  bonusId: z.string().optional(),
  ...AppliedEdgeFields,
});
export type BetRequest = z.infer<typeof BetRequest>;

export const WinRequest = WalletBaseRequest.extend({
  roundId: z.uuid(),
  txId: z.uuid(),
  /** Zero is a legal win: it closes the round on the operator side. */
  amount: MinorUnits,
  /** The debit this payout belongs to, for operator-side reconciliation. */
  betTxId: z.uuid(),
  /** False only if we expect to credit more for the same round later. */
  roundClosed: z.boolean().default(true),
  ...AppliedEdgeFields,
});
export type WinRequest = z.infer<typeof WinRequest>;

export const RollbackRequest = WalletBaseRequest.extend({
  roundId: z.uuid(),
  /** New idempotency key for the compensating entry. */
  txId: z.uuid(),
  /** The bet transaction being cancelled. */
  refTxId: z.uuid(),
  amount: MinorUnits,
});
export type RollbackRequest = z.infer<typeof RollbackRequest>;

/**
 * Operator-side rejection reasons. Anything not in this list, plus any 5xx or
 * transport failure, is treated as "unknown outcome" and drives the recovery
 * path rather than a player-visible error.
 */
export const WalletRejectionCode = z.enum([
  'INSUFFICIENT_FUNDS',
  'PLAYER_NOT_FOUND',
  'PLAYER_BLOCKED',
  'INVALID_TOKEN',
  'CURRENCY_MISMATCH',
  'BET_LIMIT_EXCEEDED',
  'ROUND_NOT_FOUND',
  'INTERNAL_ERROR',
]);
export type WalletRejectionCode = z.infer<typeof WalletRejectionCode>;

export const WalletSuccessResponse = z.object({
  status: z.literal('OK'),
  /** Player balance in minor units *after* applying this transaction. */
  balance: MinorUnits,
  /** Operator's own transaction reference, stored for reconciliation. */
  operatorTxId: z.string().optional(),
});
export type WalletSuccessResponse = z.infer<typeof WalletSuccessResponse>;

export const WalletErrorResponse = z.object({
  status: WalletRejectionCode,
  message: z.string().optional(),
  balance: MinorUnits.optional(),
});
export type WalletErrorResponse = z.infer<typeof WalletErrorResponse>;

export const WalletResponse = z.union([WalletSuccessResponse, WalletErrorResponse]);
export type WalletResponse = z.infer<typeof WalletResponse>;

export const WALLET_ENDPOINTS = {
  balance: '/balance',
  bet: '/bet',
  win: '/win',
  rollback: '/rollback',
} as const;

export type WalletOperation = keyof typeof WALLET_ENDPOINTS;

/**
 * Rejections that are the player's problem and should be surfaced verbatim,
 * versus ones that mean the integration is broken and need an alert.
 */
export const PLAYER_FACING_REJECTIONS: ReadonlySet<WalletRejectionCode> = new Set([
  'INSUFFICIENT_FUNDS',
  'PLAYER_BLOCKED',
  'BET_LIMIT_EXCEEDED',
]);

export function isWalletSuccess(response: WalletResponse): response is WalletSuccessResponse {
  return response.status === 'OK';
}
